import { useState, type JSX } from "react";
import EntityIcon from "@/components/common/EntityIcon/EntityIcon";
import Chevron from "./Chevron";
import type { LedgerRow as LedgerRowModel } from "./buildLedger";

export interface LedgerRowProps {
  row: LedgerRowModel;
  checked: boolean;
  disabled: boolean;
  disabledReason?: string;
  onToggle: (adoptId: string) => void;
}

const Tick = (): JSX.Element => (
  <svg width="11" height="11" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M4.5 10.2 8 13.7l7.5-7.5" stroke="currentColor" strokeWidth="2.4"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Lock = (): JSX.Element => (
  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <g stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4.6" y="8.9" width="10.8" height="7.5" rx="1.7" />
      <path d="M6.8 8.9V6.7a3.2 3.2 0 0 1 6.4 0v2.2" />
    </g>
  </svg>
);

const KIND_PATHS: Record<"segment" | "filter", string[]> = {
  segment: ["M3.4 4.6h13.2v10.8H3.4z", "M3.4 8.2h13.2M8.4 8.2v7.2"],
  filter: ["M3.3 5.1h13.4l-5.1 5.9v4.1l-3.2 1.6v-5.7L3.3 5.1Z"],
};

const Glyph = ({ row }: { row: LedgerRowModel }): JSX.Element => {
  const blocked = row.state === "blocked";
  // Brands and brand sets use the app's own EntityIcon so a shared link shows
  // the same marks as everywhere else. Segments and filters have no such
  // component, so they keep a stroked glyph.
  if (row.kind === "brand" || row.kind === "brandSet") {
    const entity =
      row.kind === "brandSet"
        ? { type: "BRAND_SET", color: row.color, name: row.name }
        : { name: row.name, logo: row.logo };
    return (
      <span className={`shrink-0 ${blocked ? "opacity-40 grayscale" : ""}`}>
        <EntityIcon entity={entity} size={24} />
      </span>
    );
  }
  return (
    <span
      className={`w-6 shrink-0 flex justify-center ${blocked ? "text-text-50" : "text-text-300"}`}
    >
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <g stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          {KIND_PATHS[row.kind].map((d) => <path key={d} d={d} />)}
        </g>
      </svg>
    </span>
  );
};

/**
 * One ledger line. The right rail is the only status column: an adoptable row
 * puts an interactive pill in the same slot where an inert row states its
 * status, so every row still resolves on a single axis.
 */
const LedgerRow = ({
  row, checked, disabled, disabledReason, onToggle,
}: LedgerRowProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const hasMembers = (row.members?.length ?? 0) > 0;
  const muted = row.state === "blocked";

  return (
    <div data-testid={row.id} className="border-t border-neutral-200 first:border-t-0">
      <div className="flex items-center gap-3 py-[9px] pl-0.5">
        <Glyph row={row} />

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span
              className={`text-[13.5px] font-medium tracking-[-0.01em] truncate ${muted ? "text-text-50" : "text-text-950"}`}
            >
              {row.name}
            </span>
            {row.meta ? (
              <span className="text-[11.5px] text-text-50 whitespace-nowrap">{row.meta}</span>
            ) : null}
            {hasMembers ? (
              <button
                type="button"
                aria-expanded={open}
                aria-label={`Show what ${row.name} contains`}
                onClick={() => setOpen((v) => !v)}
                className="shrink-0 self-center p-0.75 rounded-[4px] text-text-50 hover:text-text-300 hover:bg-neutral-100 cursor-pointer"
              >
                <Chevron open={open} />
              </button>
            ) : null}
          </div>
          {row.detail ? (
            <div className="text-[11.5px] text-text-50 mt-0.5">{row.detail}</div>
          ) : null}
          {row.state === "adoptable" && disabled && disabledReason ? (
            <div className="text-[11.5px] text-text-50 mt-0.5">{disabledReason}</div>
          ) : null}
        </div>

        {row.state === "adoptable" && row.adoptId ? (
          <button
            type="button"
            aria-pressed={checked}
            disabled={disabled}
            onClick={() => onToggle(row.adoptId as string)}
            /* Selected reads as a tint, not a fill: the footer's primary button
               is the only saturated element, so it stays the obvious terminus. */
            className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold whitespace-nowrap px-[11px] py-1 rounded-[20px] border transition cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${
              checked
                ? "bg-primary-soft border-primary-500 text-primary-500"
                : "bg-white border-neutral-300 text-text-300 hover:border-neutral-400"
            }`}
          >
            {checked ? <Tick /> : null}
            {checked ? "New" : "Add"}
          </button>
        ) : row.state === "blocked" ? (
          <span className="inline-flex items-center gap-[5px] text-[11.5px] font-medium text-caution-500 whitespace-nowrap">
            <Lock />
            {row.statusLabel}
          </span>
        ) : (
          <span className="text-[11.5px] text-text-50 whitespace-nowrap">{row.statusLabel}</span>
        )}
      </div>

      {hasMembers && open ? (
        <div className="text-[11.5px] leading-[1.55] text-text-300 pl-[38px] pb-2.5 -mt-[3px]">
          {row.members?.join(", ")}
        </div>
      ) : null}
    </div>
  );
};

export default LedgerRow;
